export type CanonicalFieldKind =
  | 'string'
  | 'number'
  | 'boolean'
  | 'enum'
  | 'literal';

type EnumFieldDefinition = {
  kind: 'enum';
  values: readonly string[];
  optional?: boolean;
  description?: string;
};

type LiteralFieldDefinition = {
  kind: 'literal';
  value: string | number | boolean;
  optional?: boolean;
  description?: string;
};

type PrimitiveFieldDefinition = {
  kind: Exclude<CanonicalFieldKind, 'enum' | 'literal'>;
  optional?: boolean;
  description?: string;
};

export type CanonicalFieldDefinition =
  | EnumFieldDefinition
  | LiteralFieldDefinition
  | PrimitiveFieldDefinition;

export interface CanonicalEventDefinition {
  name: string;
  description: string;
  properties: Record<string, CanonicalFieldDefinition>;
  allowAdditionalProperties?: boolean;
}

export const canonicalEvents = [
  {
    name: 'submission_started',
    description: 'User began a submission flow',
    properties: {
      submissionId: { kind: 'string' },
      templateId: { kind: 'string', optional: true },
      formType: { kind: 'enum', values: ['template', 'custom'] },
      userType: { kind: 'enum', values: ['authenticated', 'guest'], optional: true },
      organizationId: { kind: 'string', optional: true },
      conversionStep: { kind: 'literal', value: 'engagement' }
    }
  },
  {
    name: 'submission_completed',
    description: 'Submission finished successfully',
    properties: {
      submissionId: { kind: 'string' },
      channel: { kind: 'string', optional: true },
      completionTime: { kind: 'number', optional: true },
      conversionStep: { kind: 'literal', value: 'conversion' }
    }
  },
  {
    name: 'form_opened',
    description: 'User opened a form or template',
    properties: {
      submissionId: { kind: 'string' },
      templateId: { kind: 'string', optional: true },
      organizationId: { kind: 'string', optional: true }
    }
  },
  {
    name: 'invite_sent',
    description: 'Application or document invite sent to a recipient',
    properties: {
      inviteId: { kind: 'string' },
      organizationId: { kind: 'string', optional: true },
      method: { kind: 'string', optional: true },
      conversionStep: { kind: 'enum', values: ['invitation', 'activation', 'awareness'], optional: true }
    }
  },
  {
    name: 'invite_opened',
    description: 'Recipient opened an invitation email or link',
    properties: {
      inviteId: { kind: 'string' },
      userAgent: { kind: 'string', optional: true },
      conversionStep: { kind: 'literal', value: 'awareness' }
    }
  },
  {
    name: 'org_signup',
    description: 'Organization signup completed',
    properties: {
      orgId: { kind: 'string' },
      plan: { kind: 'string', optional: true }
    }
  },
  {
    name: 'document_uploaded',
    description: 'User uploaded a document',
    properties: {
      documentType: { kind: 'string', optional: true },
      fileSize: { kind: 'number', optional: true },
      uploadMethod: { kind: 'string', optional: true },
      action: { kind: 'string', optional: true }
    }
  },
  {
    name: 'document_updated',
    description: 'User updated or replaced a document',
    properties: {
      documentType: { kind: 'string', optional: true },
      fileSize: { kind: 'number', optional: true },
      updateMethod: { kind: 'string', optional: true },
      action: { kind: 'string', optional: true }
    }
  },
  {
    name: 'expiry_notice_sent',
    description: 'An expiry reminder was sent to a user',
    properties: {
      reminderType: { kind: 'string' },
      documentType: { kind: 'string', optional: true },
      daysUntilExpiry: { kind: 'number', optional: true },
      channel: { kind: 'enum', values: ['email', 'push'], optional: true },
      urgency: { kind: 'enum', values: ['low', 'medium', 'high'], optional: true }
    }
  },
  {
    name: 'billing_checkout_started',
    description: 'Billing checkout started for an organization',
    properties: {
      organizationId: { kind: 'string' },
      priceId: { kind: 'string' },
      planName: { kind: 'string', optional: true },
      conversionStep: { kind: 'literal', value: 'checkout_started' }
    }
  },
  {
    name: 'billing_checkout_completed',
    description: 'Billing checkout completed for an organization',
    properties: {
      organizationId: { kind: 'string' },
      subscriptionId: { kind: 'string' },
      planName: { kind: 'string', optional: true },
      conversionStep: { kind: 'literal', value: 'purchase_completed' }
    }
  },
  {
    name: 'billing_subscription_cancelled',
    description: 'Subscription cancelled by an organization',
    properties: {
      organizationId: { kind: 'string' },
      subscriptionId: { kind: 'string' },
      reason: { kind: 'string', optional: true },
      conversionStep: { kind: 'literal', value: 'churn' }
    }
  },
  {
    name: 'vault_document_downloaded',
    description: 'Vault document downloaded by a user',
    properties: {
      documentId: { kind: 'string' },
      documentType: { kind: 'string', optional: true },
      organizationId: { kind: 'string', optional: true },
      timestamp: { kind: 'number', optional: true }
    }
  },
  {
    name: 'pdf_intake_failed',
    description: 'PDF intake failed for an organization or user',
    properties: {
      errorCode: { kind: 'string' },
      errorMessage: { kind: 'string', optional: true },
      organizationId: { kind: 'string', optional: true },
      timestamp: { kind: 'number', optional: true }
    }
  },
  {
    name: 'org_invite_sent',
    description: 'Organization invite sent',
    properties: {
      invitationId: { kind: 'string' },
      organizationId: { kind: 'string' },
      recipientEmail: { kind: 'string', optional: true },
      timestamp: { kind: 'number', optional: true }
    }
  },
  {
    name: 'org_invite_accepted',
    description: 'Organization invite accepted',
    properties: {
      invitationId: { kind: 'string' },
      organizationId: { kind: 'string' },
      timestamp: { kind: 'number', optional: true }
    }
  },
  {
    name: 'usage_pdf_filled',
    description: 'Usage counter for PDFs filled',
    properties: {
      event_type: { kind: 'literal', value: 'pdf_filled' },
      formId: { kind: 'string', optional: true },
      organizationId: { kind: 'string', optional: true }
    }
  },
  {
    name: 'usage_pdf_export',
    description: 'Usage counter for PDF exports',
    properties: {
      event_type: { kind: 'literal', value: 'pdf_export' },
      formId: { kind: 'string', optional: true },
      organizationId: { kind: 'string', optional: true }
    }
  },
  {
    name: 'usage_form_submitted',
    description: 'Usage counter for completed form submissions',
    properties: {
      event_type: { kind: 'literal', value: 'form_submitted' },
      submissionId: { kind: 'string', optional: true },
      organizationId: { kind: 'string', optional: true }
    }
  },
  {
    name: 'usage_invite_sent',
    description: 'Usage counter for invites sent',
    properties: {
      event_type: { kind: 'literal', value: 'invite_sent' },
      inviteId: { kind: 'string', optional: true },
      organizationId: { kind: 'string', optional: true }
    }
  }
] as const satisfies readonly CanonicalEventDefinition[];

export type CanonicalEventName = typeof canonicalEvents[number]['name'];

export function createCanonicalEventSchemas(z: any) {
  if (!z?.object || !z?.literal || !z?.enum) {
    throw new Error('Invalid Zod instance provided to createCanonicalEventSchemas');
  }

  const eventNames = canonicalEvents.map((event) => event.name) as [string, ...string[]];
  const canonicalEventEnum = z.enum(eventNames);

  const canonicalEventSchemas = canonicalEvents.reduce<Record<string, any>>((acc, event) => {
    const shape: Record<string, any> = {};

    Object.entries(event.properties).forEach(([key, definition]) => {
      let fieldSchema: any;
      switch (definition.kind) {
        case 'string':
          fieldSchema = z.string();
          break;
        case 'number':
          fieldSchema = z.number();
          break;
        case 'boolean':
          fieldSchema = z.boolean();
          break;
        case 'enum':
          fieldSchema = z.enum([...definition.values] as [string, ...string[]]);
          break;
        case 'literal':
          fieldSchema = z.literal(definition.value);
          break;
        default:
          fieldSchema = z.unknown();
      }

      if (definition.optional) {
        fieldSchema = fieldSchema.optional();
      }

      shape[key] = fieldSchema;
    });

    const schema = z.object(shape);
    acc[event.name] = schema.passthrough();
    return acc;
  }, {});

  const analyticsEventSchema = z.discriminatedUnion(
    'name',
    canonicalEvents.map((event) =>
      z.object({
        name: z.literal(event.name),
        properties: canonicalEventSchemas[event.name],
      }),
    ),
  );

  return {
    canonicalEventEnum,
    canonicalEventSchemas: canonicalEventSchemas as Record<CanonicalEventName, any>,
    analyticsEventSchema,
  };
}
